import { useState, useEffect, useCallback } from "react";
import { Controller, UseFormRegister } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
// COMPONENTS
import {
	BaseButton,
	BaseInput,
	UpdatedAdvancedInput,
	BaseToolTip,
} from "../../../../../../components";

import {
	getAdminsAndManagers,
	getAdmins,
	getSearchValuesFromAdvancedInput,
} from "../../../../api/OrdersApi";

import { getFontAwesomeIcon } from "../../../../../../utils";
import { FieldsAndApprovalDecisions, FormValues } from "./ApprovalFlowForm";
// TYPES
type ApprovalFlowDetailInputsProps = {
	selectedStage: FieldsAndApprovalDecisions;
	reset: (values: FormValues) => void;
	getValues: () => FormValues;
	setValue: (name: string, value: any) => void;
	userBadgeClassNames: string;
	onClose: () => void;
	register: UseFormRegister<any>;
	isApproveButtonEnabled: boolean;
};

const ApprovalFlowDetailInputs: React.FC<ApprovalFlowDetailInputsProps> = ({
	selectedStage,
	reset,
	getValues,
	setValue,
	userBadgeClassNames,
	onClose,
	register,
	isApproveButtonEnabled,
}) => {
	///////////////////////////////////////////////////////////////////// ADVANCED INPUT LOGIC //////////////////////////////////////////////////////////////////////////////
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [userOptions, setUserOptions] = useState<any[]>([]);
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const [hasMorePages, setHasMorePages] = useState(true);

	// Determine if we need only admins or admins and managers based on allowed roles
	const needsAdminsOnly =
		selectedStage.allowedRoles.length === 1 &&
		selectedStage.allowedRoles.includes("Admin");

	// Fetch admins using useQuery (for admin-only stages)
	const { data: admins, isLoading: isLoadingAdmins } = useQuery({
		queryKey: ["admins"],
		queryFn: getAdmins,
		enabled: needsAdminsOnly,
	});

	// Initialize data based on role requirements
	useEffect(() => {
		if (needsAdminsOnly && admins) {
			// Use admins from useQuery for admin-only stages
			setUserOptions(admins);
			setHasMorePages(false);
		} else if (!needsAdminsOnly) {
			// Fetch paginated admins and managers for other stages
			const fetchInitialData = async () => {
				setIsFetchingMore(true);
				try {
					const initialData = await getAdminsAndManagers(100, 1);
					setUserOptions(initialData);
					setCurrentPage(2);
				} catch (error) {
					console.error("Error loading initial data", error);
				} finally {
					setIsFetchingMore(false);
				}
			};
			fetchInitialData();
		}
	}, [needsAdminsOnly, admins]);

	// Fetch more data for paginated results
	const fetchMoreData = useCallback(async (): Promise<void> => {
		if (isFetchingMore || !hasMorePages || needsAdminsOnly) return;

		setIsFetchingMore(true);
		try {
			const moreData = await getAdminsAndManagers(100, currentPage);

			if (moreData.length === 0) {
				setHasMorePages(false);
			} else {
				setUserOptions((prevData) => [...prevData, ...moreData]);
				setCurrentPage((prev) => prev + 1);
			}
		} catch (error) {
			console.error("Error loading more users", error);
		} finally {
			setIsFetchingMore(false);
		}
	}, [isFetchingMore, hasMorePages, currentPage, needsAdminsOnly]);

	const findSpecificValue = useCallback(
		async (inputValue: string): Promise<any> => {
			setIsFetchingMore(true);
			try {
				const result = await getSearchValuesFromAdvancedInput(
					1000,
					1,
					inputValue,
					selectedStage.allowedRoles,
				);
				return result;
			} catch (error) {
				console.error("Error finding specific value", error);
				return null;
			} finally {
				setIsFetchingMore(false);
			}
		},
		[selectedStage, needsAdminsOnly],
	);

	const handleClearInput = (): void => {
		const fieldPath = `${selectedStage.valueKey}.assignedTo` as const;
		setValue(fieldPath, { uuid: "", name: "" });
	};

	const handleUserSelect = (user: any) => {
		const fieldPath = `${selectedStage.valueKey}.assignedTo` as const;

		setValue(fieldPath, { uuid: user.uuid, name: user.name });
	};

	const allValues = getValues();
	const actionButtonValue =
		allValues[selectedStage.valueKey]?.actionButtonSelected;

	let isDisabledToolTipOn = false;

	if (selectedStage.valueKey === "1" && isApproveButtonEnabled === false) {
		isDisabledToolTipOn = true;
	}

	let showStageActionButtons = true;

	if (selectedStage?.valueKey === "2" && selectedStage.isPreApproved) {
		showStageActionButtons = false;
	}

	return (
		<>
			<div className="flex flex-col gap-y-6">
				{selectedStage?.inputs?.map((input: any) => {
					return input.inputType === "advancedInput" ? (
						<div className="w-full flex flex-col gap-y-3">
							<Controller
								name={`${selectedStage.valueKey}.assignedTo`}
								key={selectedStage.valueKey}
								render={({ field }) => (
									<UpdatedAdvancedInput
										label={input.label}
										onSelect={(value) => {
											field.onChange(value);
											handleUserSelect(value);
										}}
										labelClasses={input.labelTextClasses}
										fetchMoreData={fetchMoreData}
										findSpecificValue={findSpecificValue}
										data={userOptions}
										isFetchingMore={
											isFetchingMore || isLoadingAdmins
										}
										hasMorePages={hasMorePages}
										onClearInput={() => {
											field.onChange(null);
											handleClearInput();
										}}
										value={field.value || null}
									/>
								)}
							/>

							<div className="font-omnes-regular">
								<span className="text-sm">
									Eligible Approvers:{" "}
								</span>

								{selectedStage.allowedRoles.length > 0 &&
									selectedStage.allowedRoles.map(
										(role: string) => (
											<span
												key={role}
												className={userBadgeClassNames}
											>
												{role}
											</span>
										),
									)}
							</div>
						</div>
					) : (
						<Controller
							name={`${selectedStage.valueKey}.note`}
							key={selectedStage.valueKey}
							render={({ field }) => (
								<BaseInput
									field={{
										...input,
										valueKey: `${selectedStage.valueKey}.note`,
									}}
								/>
							)}
						/>
					);
				})}
				{showStageActionButtons && (
					<div className="flex flex-col w-full gap-y-2">
						<div className="flex items-center font-omnes-medium text-navy-500 ">
							Stage Actions
						</div>
						{actionButtonValue === null && (
							<div className="flex flex-col gap-y-2">
								<label
									className={`font-omnes-medium text-md w-full ${isDisabledToolTipOn ? "cursor-not-allowed" : "cursor-pointer"}`}
								>
									<BaseToolTip
										isToolTipOn={isDisabledToolTipOn}
										message={"Needs an Assigned Manager"}
										childrenContainerClasses="w-full relative flex items-center justify-center"
										toolTipPlacement="top"
										children={
											<>
												<input
													type="radio"
													className="peer sr-only"
													value="1"
													disabled={
														selectedStage.valueKey ===
															"1" &&
														!isApproveButtonEnabled
													}
													{...register(
														`${selectedStage.valueKey}.actionButtonSelected`,
													)}
												/>
												<div
													className={`w-full h-10 rounded-lg border-1  flex items-center justify-center  gap-x-2 transition-all ${isDisabledToolTipOn ? "bg-gray-300 text-gray-700 border-gray-300" : "cursor-pointer border-mint-700 bg-transparent peer-checked:bg-mint-700 peer-checked:border-mint-700 hover:bg-mint-50 peer-checked:text-white text-mint-700"}`}
												>
													<span>
														{getFontAwesomeIcon(
															"check",
															"regular",
														)}
													</span>
													{"Approve This Stage"}
												</div>
											</>
										}
									/>
								</label>

								<label className="relative cursor-pointer font-omnes-medium text-md">
									<input
										type="radio"
										className="peer sr-only"
										value="0"
										{...register(
											`${selectedStage.valueKey}.actionButtonSelected`,
										)}
									/>
									<div className="w-full h-10 rounded-lg border-1 border-crimson-700 bg-transparent flex items-center justify-center gap-x-2 transition-all peer-checked:bg-crimson-700 peer-checked:border-crimson-700 hover:hover:bg-crimson-50 peer-checked:text-white text-crimson-700">
										<span>
											{getFontAwesomeIcon(
												"xmark",
												"regular",
											)}
										</span>
										{"Deny This Stage"}
									</div>
								</label>
							</div>
						)}

						{actionButtonValue !== null && (
							<BaseButton
								type="button"
								text={"Reset to Pending"}
								buttonSize="h-10"
								fontColor="text-gray-700"
								fontFamily="font-omnes-medium"
								additionalClasses="flex items-center justify-center border-1 border-gray-700 px-2 hover:bg-gray-50 text-nowrap max-sm:size-8 max-sm:justify-center text-md"
								onClick={() =>
									setValue(
										`${selectedStage.valueKey}.actionButtonSelected`,
										null,
									)
								}
								shape="rounded-md"
								icon={getFontAwesomeIcon(
									"rotateLeft",
									"regular",
								)}
								iconClasses="mr-2"
							/>
						)}
					</div>
				)}
			</div>
		</>
	);
};

export default ApprovalFlowDetailInputs;
